import { useCallback, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { controlClass } from './Field'

/** The row of filters under a page header. Filter values live in the URL
 *  query (useQueryParam), so a filtered view survives a reload and can be
 *  linked from a Telegram notice. */
export function FilterBar({ children, end, className }: { children: ReactNode; end?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {children}
      {end && <div className="ml-auto flex items-center gap-2">{end}</div>}
    </div>
  )
}

/** One query parameter as state. Setting the default value removes it from
 *  the URL; other parameters are kept. */
export function useQueryParam(key: string, fallback = ''): [string, (value: string) => void] {
  const [params, setParams] = useSearchParams()
  const value = params.get(key) ?? fallback
  const set = useCallback(
    (next: string) => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          if (!next || next === fallback) p.delete(key)
          else p.set(key, next)
          return p
        },
        { replace: true },
      )
    },
    [key, fallback, setParams],
  )
  return [value, set]
}

/** Several query parameters in one navigation; an empty value removes one.
 *  Two useQueryParam setters called in one handler do not add up: each
 *  updater gets the params of the last render, so the second write puts
 *  back what the first one changed. */
export function useSetQueryParams(): (updates: Record<string, string>) => void {
  const [, setParams] = useSearchParams()
  return useCallback(
    (updates: Record<string, string>) => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          for (const [key, next] of Object.entries(updates)) {
            if (next) p.set(key, next)
            else p.delete(key)
          }
          return p
        },
        { replace: true },
      )
    },
    [setParams],
  )
}

export interface FilterSelectProps {
  label: string
  value: string
  onChange: (value: string) => void
  options: { value: string; label: string }[]
  className?: string
}

/** A compact select for the filter row. The label is for screen readers;
 *  the first option ("앱 전체") says what an empty value means. */
export function FilterSelect({ label, value, onChange, options, className }: FilterSelectProps) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(controlClass, 'h-8 w-auto max-w-[14rem] pr-8 text-table', className)}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

export function SearchInput({
  value,
  onChange,
  placeholder = '찾기',
  className,
}: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
}) {
  return (
    <label className={cn('relative block', className)}>
      <span className="sr-only">{placeholder}</span>
      <Search
        size={14}
        className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(controlClass, 'h-8 w-52 pl-8 text-table')}
      />
    </label>
  )
}
